import type { CharacterId } from "../game/GameBus";
import type { LevelNumber } from "../game/levels";
import {
  getAccessoriesForCharacter,
  getAccessoryForCharacter,
  isAccessoryUnlocked,
  type AccessoryId,
} from "../game/wardrobe";
import { CharacterPreview } from "./CharacterPreview";
import { ReplayIcon } from "./Icons";

interface WardrobeScreenProps {
  character: CharacterId;
  level: LevelNumber;
  totalLevels: number;
  completedLevelIds: readonly string[];
  equippedAccessoryId: AccessoryId | null;
  newlyUnlockedAccessoryId: AccessoryId | null;
  onEquip: (accessoryId: AccessoryId | null) => void;
  onBack: () => void;
  onContinue: () => void;
}

export function WardrobeScreen({
  character,
  level,
  totalLevels,
  completedLevelIds,
  equippedAccessoryId,
  newlyUnlockedAccessoryId,
  onEquip,
  onBack,
  onContinue,
}: WardrobeScreenProps) {
  const accessories = getAccessoriesForCharacter(character);
  const equipped = getAccessoryForCharacter(character, equippedAccessoryId);
  const isFinalLevel = level >= totalLevels;

  return (
    <main className={`wardrobe-screen ${character}`}>
      <div className="wardrobe-backdrop" />
      <section className="wardrobe-shell" aria-labelledby="wardrobe-title">
        <div className="wardrobe-preview-panel">
          <div>
            <p className="wardrobe-eyebrow">פרס על שלב {level}</p>
            <h1 id="wardrobe-title">בוחרים לבוש</h1>
            <p className="wardrobe-subtitle">אפשר לבחור פריט חדש או להישאר בלי אביזר</p>
          </div>
          <CharacterPreview
            character={character}
            accessoryId={equippedAccessoryId}
            className="wardrobe-hero-preview"
          />
          <strong className="equipped-label">
            {equipped ? `לבוש נבחר: ${equipped.displayName}` : "לבוש נבחר: ללא אביזר"}
          </strong>
        </div>

        <div className="wardrobe-picker-panel">
          <div className="wardrobe-grid" role="radiogroup" aria-label="בחירת אביזר לדמות">
            <button
              type="button"
              className={`wardrobe-card no-accessory ${equippedAccessoryId === null ? "selected" : ""}`}
              role="radio"
              aria-checked={equippedAccessoryId === null}
              onClick={() => onEquip(null)}
            >
              <CharacterPreview character={character} decorative className="wardrobe-card-preview" />
              <span>ללא אביזר</span>
              <small>תמיד זמין</small>
            </button>

            {accessories.map((accessory) => {
              const unlocked = isAccessoryUnlocked(character, accessory.id, completedLevelIds);
              const selected = equippedAccessoryId === accessory.id;
              const isNew = newlyUnlockedAccessoryId === accessory.id;
              return (
                <button
                  type="button"
                  key={accessory.id}
                  className={`wardrobe-card ${selected ? "selected" : ""} ${isNew ? "new" : ""}`}
                  role="radio"
                  aria-checked={selected}
                  aria-label={unlocked
                    ? `${accessory.displayName}, ${selected ? "נבחר" : "זמין"}`
                    : `${accessory.displayName}, ייפתח אחרי שלב ${accessory.unlockAfterLevel}`}
                  disabled={!unlocked}
                  onClick={() => onEquip(accessory.id)}
                >
                  {isNew ? <b className="new-accessory-badge">חדש!</b> : null}
                  <CharacterPreview
                    character={character}
                    accessoryId={accessory.id}
                    decorative
                    className="wardrobe-card-preview"
                  />
                  <span>{accessory.displayName}</span>
                  <small>{unlocked ? accessory.categoryDisplayName : `🔒 אחרי שלב ${accessory.unlockAfterLevel}`}</small>
                </button>
              );
            })}
          </div>

          <div className="wardrobe-actions">
            <button type="button" className="secondary-button" onClick={onBack}>
              <ReplayIcon />חזרה לתוצאות
            </button>
            <button type="button" className="primary-button" onClick={onContinue}>
              {isFinalLevel ? "סיום המסע" : `לשלב ${level + 1}`}
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
